import type { ReactNode } from "react";

import type { IntlShape } from "react-intl";

import type { PriceLineItemProps } from "@/components/molecules/PriceLineItem";
import { getSupportArticleUrl } from "@/lib/utils/support-utils";

import { AddOnsDetailContent } from "../../AddOnsDetailContent";
import {
  tooltipContentMessages,
  tooltipLinkMessages,
  tooltipTitleMessages,
} from "../../priceSummaryTooltip.messages";
import { DetailIds, TOOLTIP_SUPPORT_ARTICLES } from "../consts";
import type { DetailId, PriceFacts, TooltipId } from "../types";
import { buildAddOnsBreakdown } from "./addOnsBreakdown";
import { formatLabelRef } from "./formatLabelRef";

type RenderPricingRulesLink = (
  linkLabel: ReactNode,
  closeTooltip: () => void,
) => ReactNode;

type ToPriceLineItemTooltipParams = {
  currency: string;
  detailId?: DetailId;
  facts?: PriceFacts | null;
  intl: IntlShape;
  renderPricingRulesLink?: RenderPricingRulesLink;
  tooltipId?: TooltipId;
};

// toPriceLineItemTooltip is the main function at the bottom of the file

/** Row/message lookups shared by every explainer-text tooltip (not the add-ons modal). */
type ExplainerContext = {
  explainerText: string;
  linkLabel?: string;
  title?: string;
  tooltipId?: TooltipId;
};

/**
 * Row-detail explainer: the Add-ons total row opens a modal itemizing each
 * add-on, built from `facts` rather than a message catalog.
 */
const buildAddOnsTooltip = ({
  currency,
  detailId,
  facts,
  intl,
}: {
  currency: string;
  detailId?: DetailId;
  facts?: PriceFacts | null;
  intl: IntlShape;
}): PriceLineItemProps["tooltip"] | undefined => {
  if (
    detailId !== DetailIds.AddOnsTotal ||
    facts?.addOns == null ||
    facts.addOns.length === 0
  ) {
    return undefined;
  }

  const model = buildAddOnsBreakdown({
    addOns: facts.addOns,
    addOnsTotal: facts.addOnsTotal ?? 0,
  });

  return {
    presentation: "modal",
    renderContent: (setListOpen) => (
      <AddOnsDetailContent
        currency={currency}
        intl={intl}
        model={model}
        onListOpenChange={setListOpen}
      />
    ),
    title: formatLabelRef({ currency, intl, ref: { id: model.titleId } }),
  };
};

/**
 * Resolves the message-catalog lookups shared by both link tooltips and the
 * plain explainer. Returns `undefined` when this row has no tooltip copy at
 * all (no `tooltipId`/`detailId`, or no matching `tooltipContentMessages`
 * entry).
 */
const resolveExplainerContext = ({
  detailId,
  intl,
  tooltipId,
}: {
  detailId?: DetailId;
  intl: IntlShape;
  tooltipId?: TooltipId;
}): ExplainerContext | undefined => {
  const explainerId = tooltipId ?? detailId;
  if (!explainerId) return undefined;

  const titleDescriptor =
    tooltipTitleMessages[explainerId as keyof typeof tooltipTitleMessages];
  const contentDescriptor =
    tooltipContentMessages[explainerId as keyof typeof tooltipContentMessages];
  if (!contentDescriptor) return undefined;

  const linkDescriptor = tooltipId
    ? tooltipLinkMessages[tooltipId as keyof typeof tooltipLinkMessages]
    : undefined;

  return {
    explainerText: intl.formatMessage(contentDescriptor),
    linkLabel: linkDescriptor ? intl.formatMessage(linkDescriptor) : undefined,
    title: titleDescriptor ? intl.formatMessage(titleDescriptor) : undefined,
    tooltipId,
  };
};

/**
 * Help Center link tooltip. Needs no adapter — the locale comes from the
 * `intl` instance the call site already passes in.
 */
const buildSupportArticleTooltip = ({
  context,
  intl,
}: {
  context: ExplainerContext;
  intl: IntlShape;
}): PriceLineItemProps["tooltip"] | undefined => {
  const { explainerText, linkLabel, title, tooltipId } = context;
  if (linkLabel == null || !tooltipId) return undefined;

  const supportArticle =
    TOOLTIP_SUPPORT_ARTICLES[
      tooltipId as keyof typeof TOOLTIP_SUPPORT_ARTICLES
    ];
  if (!supportArticle) return undefined;

  return {
    title,
    content: (
      <>
        {explainerText}{" "}
        <a
          className="tw-underline"
          href={getSupportArticleUrl(supportArticle, intl.locale)}
          rel="noopener noreferrer"
          target="_blank"
        >
          {linkLabel}
        </a>
      </>
    ),
  };
};

/**
 * Pricing-rules link tooltip: the call site wraps the link and gets a
 * `closeTooltip` callback to dismiss the popover before opening its own
 * dialog (e.g. the DIY space rental "View pricing rules" link).
 */
const buildPricingRulesLinkTooltip = ({
  context,
  renderPricingRulesLink,
}: {
  context: ExplainerContext;
  renderPricingRulesLink?: RenderPricingRulesLink;
}): PriceLineItemProps["tooltip"] | undefined => {
  const { explainerText, linkLabel, title } = context;
  if (linkLabel == null || !renderPricingRulesLink) return undefined;

  return {
    title,
    content: (closeTooltip) => (
      <>
        {explainerText} {renderPricingRulesLink(linkLabel, closeTooltip)}
      </>
    ),
  };
};

/** Plain explainer text, no link. Detail ids open as modal; tooltip ids stay popover. */
const buildPlainTooltip = ({
  context,
  detailId,
  tooltipId,
}: {
  context: ExplainerContext;
  detailId?: DetailId;
  tooltipId?: TooltipId;
}): PriceLineItemProps["tooltip"] => ({
  title: context.title,
  content: context.explainerText,
  ...(detailId && !tooltipId ? { presentation: "modal" as const } : {}),
});

/**
 * Currently, tooltips are handling various types of content.
 * We have the AddOnsBreakdown tooltip which opens a modal with the add-ons breakdown,
 * the help center link tooltip which opens a new tab with the help center article,
 * the pricing-rules link tooltip which lets the call site close the popover
 * and open its own dialog, and the plain explainer text tooltip.
 */
const toPriceLineItemTooltip = ({
  currency,
  detailId,
  facts,
  intl,
  renderPricingRulesLink,
  tooltipId,
}: ToPriceLineItemTooltipParams): PriceLineItemProps["tooltip"] => {
  // Add-ons total row with itemized add-ons: opens the breakdown modal.
  const addOnsTooltip = buildAddOnsTooltip({ currency, detailId, facts, intl });
  if (addOnsTooltip) return addOnsTooltip;

  // No catalog copy for this row's tooltipId/detailId — no tooltip at all.
  const context = resolveExplainerContext({ detailId, intl, tooltipId });
  if (!context) return undefined;

  // tooltipId has both a link label and a Help Center article.
  const supportArticleTooltip = buildSupportArticleTooltip({ context, intl });
  if (supportArticleTooltip) return supportArticleTooltip;

  // tooltipId has a link label and the caller supplied a renderer for it.
  const pricingRulesLinkTooltip = buildPricingRulesLinkTooltip({
    context,
    renderPricingRulesLink,
  });
  if (pricingRulesLinkTooltip) return pricingRulesLinkTooltip;

  // Fallback: plain explainer text, no link.
  return buildPlainTooltip({ context, detailId, tooltipId });
};

export { toPriceLineItemTooltip };
